import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/app/ProtectedRoute';
import AdminRoute from './components/app/AdminRoute';
import ScrollToTop from './components/ScrollToTop';
import ImpersonationBanner from './components/app/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';

// ── Eager: always needed immediately ─────────────────────────────────────────
import CompanyHome from './pages/CompanyHome';

// ── Lazy: public marketing pages ─────────────────────────────────────────────
const GoalPlannerLanding  = lazy(() => import('./pages/GoalPlannerLanding'));
const AboutPage           = lazy(() => import('./pages/AboutPage'));
const ContactPage         = lazy(() => import('./pages/ContactPage'));
const FAQPage             = lazy(() => import('./pages/FAQPage'));
const PrivacyPolicy       = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions     = lazy(() => import('./components/TermsConditions'));
const AccountDeletion     = lazy(() => import('./components/AccountDeletion'));
const NotFound            = lazy(() => import('./components/NotFound'));

// ── Lazy: auth ────────────────────────────────────────────────────────────────
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const RegisterPage        = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage   = lazy(() => import('./pages/ResetPasswordPage'));

// ── Lazy: public ──────────────────────────────────────────────────────────────
const ShopPage            = lazy(() => import('./pages/ShopPage'));
const ShopProductPage     = lazy(() => import('./pages/ShopProductPage'));
const UpgradePage         = lazy(() => import('./pages/UpgradePage'));

// ── Lazy: free tools ──────────────────────────────────────────────────────────
const DebtPayoffTool      = lazy(() => import('./pages/tools/DebtPayoffTool'));
const BudgetTool          = lazy(() => import('./pages/tools/BudgetTool'));
const MpesaAnalyser       = lazy(() => import('./pages/tools/MpesaAnalyser'));
const RentalTool          = lazy(() => import('./pages/tools/RentalTool'));
const AirbnbTool          = lazy(() => import('./pages/tools/AirbnbTool'));
const PortfolioTool       = lazy(() => import('./pages/tools/PortfolioTool'));
const BookkeepingTool     = lazy(() => import('./pages/tools/BookkeepingTool'));
const InvoiceTool         = lazy(() => import('./pages/tools/InvoiceTool'));
const InventoryTool       = lazy(() => import('./pages/tools/InventoryTool'));
const ChamaTool           = lazy(() => import('./pages/tools/ChamaTool'));
const ForexTool           = lazy(() => import('./pages/tools/ForexTool'));
const StudentBudgetTool   = lazy(() => import('./pages/tools/StudentBudgetTool'));
const WeightLossTool      = lazy(() => import('./pages/tools/WeightLossTool'));

// ── Lazy: app (authenticated) ─────────────────────────────────────────────────
const Dashboard           = lazy(() => import('./pages/app/Dashboard'));
const GoalDetail          = lazy(() => import('./pages/app/GoalDetail'));
const GoalsPage           = lazy(() => import('./pages/app/GoalsPage'));
const TransactionsPage    = lazy(() => import('./pages/app/TransactionsPage'));
const AccountSettingsPage = lazy(() => import('./pages/app/AccountSettingsPage'));
const CalculatorPage      = lazy(() => import('./pages/app/CalculatorPage'));
const DebtsPage           = lazy(() => import('./pages/app/DebtsPage'));
const BudgetPage          = lazy(() => import('./pages/app/BudgetPage'));
const RecurringPage       = lazy(() => import('./pages/app/RecurringPage'));
const InvestmentsPage     = lazy(() => import('./pages/app/InvestmentsPage'));
const PurchasesPage       = lazy(() => import('./pages/app/PurchasesPage'));
const PaymentHistoryPage  = lazy(() => import('./pages/app/PaymentHistoryPage'));
const AdminDashboard      = lazy(() => import('./pages/app/admin/AdminDashboard'));
const AdminUsers          = lazy(() => import('./pages/app/admin/AdminUsers'));
const AdminLeads          = lazy(() => import('./pages/app/admin/AdminLeads'));
const AdminShopProducts   = lazy(() => import('./pages/app/admin/AdminShopProducts'));
const AdminShopSales      = lazy(() => import('./pages/app/admin/AdminShopSales'));

const Fallback = () => (
  <div className="min-h-screen bg-paper dark:bg-ink-950 flex items-center justify-center">
    <div className="w-5 h-5 rounded-full border-2 border-teal border-t-transparent animate-spin" />
  </div>
);

// Mounted once, inside AuthProvider, so exitImpersonation() reaches a visible banner from
// every /app/* page -- not just the 5 admin pages where impersonation starts. The wrapper's
// top padding keeps page content clear of the banner's fixed overlay; AppSidebar/AppMobileTopBar
// read `impersonating` themselves to shift their own top offset (see AppNav.tsx).
function AppShell() {
  const { impersonating } = useAuth();

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <div className={impersonating ? 'pt-10' : ''}>
        <ErrorBoundary>
        <Suspense fallback={<Fallback />}>
          <Routes>
            {/* ── Company homepage ── */}
            <Route path="/" element={<CompanyHome />} />

            {/* ── Tool landing pages ── */}
            <Route path="/financial-goal-planner" element={<GoalPlannerLanding />} />
            <Route path="/app/calculator" element={<CalculatorPage />} />

            {/* ── Free tools ── */}
            <Route path="/tools/debt-payoff"     element={<DebtPayoffTool />} />
            <Route path="/tools/budget"          element={<BudgetTool />} />
            <Route path="/tools/mpesa"           element={<MpesaAnalyser />} />
            <Route path="/tools/rental"          element={<RentalTool />} />
            <Route path="/tools/airbnb"          element={<AirbnbTool />} />
            <Route path="/tools/portfolio"       element={<PortfolioTool />} />
            <Route path="/tools/bookkeeping"     element={<BookkeepingTool />} />
            <Route path="/tools/invoice"         element={<InvoiceTool />} />
            <Route path="/tools/inventory"       element={<InventoryTool />} />
            <Route path="/tools/chama"           element={<ChamaTool />} />
            <Route path="/tools/forex"           element={<ForexTool />} />
            <Route path="/tools/student-budget"  element={<StudentBudgetTool />} />
            <Route path="/tools/weight-loss"     element={<WeightLossTool />} />

            {/* ── Shop ── */}
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:sku" element={<ShopProductPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />

            {/* ── Company pages ── */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />

            {/* ── Legal ── */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/delete-account" element={<AccountDeletion />} />

            {/* ── Auth ── */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* ── Protected app ── */}
            <Route path="/app/dashboard"    element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/app/goals"        element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
            <Route path="/app/goals/:id"    element={<ProtectedRoute><GoalDetail /></ProtectedRoute>} />
            <Route path="/app/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
            <Route path="/app/settings"     element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
            <Route path="/app/debts"        element={<ProtectedRoute><DebtsPage /></ProtectedRoute>} />
            <Route path="/app/budget"       element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
            <Route path="/app/recurring"    element={<ProtectedRoute><RecurringPage /></ProtectedRoute>} />
            <Route path="/app/investments"  element={<ProtectedRoute><InvestmentsPage /></ProtectedRoute>} />
            <Route path="/app/purchases"    element={<ProtectedRoute><PurchasesPage /></ProtectedRoute>} />
            <Route path="/app/payment-history" element={<ProtectedRoute><PaymentHistoryPage /></ProtectedRoute>} />

            {/* ── Admin ── */}
            <Route path="/app/admin"              element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/app/admin/users"        element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/app/admin/leads"        element={<AdminRoute><AdminLeads /></AdminRoute>} />
            <Route path="/app/admin/shop/products" element={<AdminRoute><AdminShopProducts /></AdminRoute>} />
            <Route path="/app/admin/shop/sales"    element={<AdminRoute><AdminShopSales /></AdminRoute>} />

            {/* ── 404 ── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
      <AuthProvider>
        <SidebarProvider>
          <Router>
            <AppShell />
          </Router>
        </SidebarProvider>
      </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
